import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";

const PHP_URL     = process.env.REACT_APP_PHP_API_URL || "";
const LS_ACCESS   = "pp_access_token";
const LS_REFRESH  = "pp_refresh_token";
const LS_CAMPAIGN = "pp_selected_campaign_id";

const saveSelectedCampaignId = (id) =>
  id != null ? localStorage.setItem(LS_CAMPAIGN, String(id)) : localStorage.removeItem(LS_CAMPAIGN);

const getSavedCampaignId = () => {
  const v = localStorage.getItem(LS_CAMPAIGN);
  return v ? parseInt(v) : null;
};

const pickCampaign = (camps) => {
  const savedId = getSavedCampaignId();
  if (savedId) {
    const saved = camps.find((c) => c.id === savedId);
    if (saved) return saved;
  }
  return camps.find((c) => c.active) || camps[0] || null;
};

const AuthContext = createContext(null);

const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
};

const isExpired = (token) => {
  const p = parseJwt(token);
  if (!p?.exp) return true;
  return p.exp * 1000 < Date.now() + 30000; // 30s buffer
};

export const AuthProvider = ({ children }) => {
  const [user,             setUser]             = useState(null);
  const [campaigns,        setCampaigns]        = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [loading,          setLoading]          = useState(true);
  const refreshingRef = useRef(false);

  // PHP magic links arrive as /auth/magic?token=...&uid=...
  const initialMagicToken = useRef((() => {
    const params = new URLSearchParams(window.location.search);

    // PHP format: ?token=...&uid=...
    const phpToken = params.get("token");
    const uid      = params.get("uid");
    if (phpToken && uid) return { token: phpToken, uid };

    // Legacy format: ?magic_token=...
    const legacyToken = params.get("magic_token")
      || (window.location.pathname.match(/[/?]magic_token=([a-f0-9]+)/i)?.[1] ?? null);
    if (legacyToken) return { token: legacyToken, uid: null };

    return null;
  })());

  // ── Token storage ──────────────────────────────────────────────────────────
  const storeTokens = (access, refresh) => {
    localStorage.setItem(LS_ACCESS, access);
    if (refresh) localStorage.setItem(LS_REFRESH, refresh);
  };

  const clearTokens = () => {
    localStorage.removeItem(LS_ACCESS);
    localStorage.removeItem(LS_REFRESH);
  };

  const getAccessToken  = () => localStorage.getItem(LS_ACCESS);
  const getRefreshToken = () => localStorage.getItem(LS_REFRESH);

  // ── Apply auth response from login / refresh / magic verify ───────────────
  // PHP returns { token, user, campaigns } — refresh token lives in JWT ref claim
  const applyAuthResponse = useCallback((data) => {
    const accessToken  = data.token ?? data.accessToken;
    const refreshToken = data.refreshToken ?? parseJwt(accessToken)?.ref ?? null;
    storeTokens(accessToken, refreshToken);
    setUser({ ...data.user, superuser: !!data.user?.superuser });
    const camps = data.campaigns || [];
    setCampaigns(camps);
    const pick = pickCampaign(camps);
    setSelectedCampaign(pick);
    saveSelectedCampaignId(pick?.id ?? null);
  }, []);

  // ── Apply /auth/me response (flat user object with campaigns embedded) ─────
  const applyMeResponse = useCallback((data) => {
    const { campaigns: camps, ...userFields } = data;
    setUser({ ...userFields, superuser: !!userFields.superuser });
    const campList = Array.isArray(camps) ? camps : [];
    setCampaigns(campList);
    const pick = pickCampaign(campList);
    setSelectedCampaign(pick);
    saveSelectedCampaignId(pick?.id ?? null);
  }, []);

  // ── Refresh access token using refresh token ───────────────────────────────
  const doRefresh = useCallback(async () => {
    if (refreshingRef.current) return false;
    const rt = getRefreshToken();
    if (!rt) return false;
    refreshingRef.current = true;
    try {
      const res = await fetch(`${PHP_URL}/auth/token/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) { clearTokens(); return false; }
      applyAuthResponse(await res.json());
      return true;
    } catch {
      return false;
    } finally {
      refreshingRef.current = false;
    }
  }, [applyAuthResponse]);

  // ── Get a valid access token (refresh if needed) ───────────────────────────
  const getValidToken = useCallback(async () => {
    const at = getAccessToken();
    if (at && !isExpired(at)) return at;
    const ok = await doRefresh();
    return ok ? getAccessToken() : null;
  }, [doRefresh]);

  // ── Authenticated fetch helper ─────────────────────────────────────────────
  const authFetch = useCallback(async (url, options = {}) => {
    const token = await getValidToken();
    // Don't force JSON content-type for multipart/FormData uploads — the browser must set the
    // multipart boundary itself (otherwise PHP won't populate $_FILES).
    const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
    return fetch(url, {
      ...options,
      headers: {
        ...(isForm ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  }, [getValidToken]);

  // ── Login ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (payload) => {
    const res = await fetch(`${PHP_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    applyAuthResponse(data);
    return data;
  }, [applyAuthResponse]);

  // ── Verify magic link token ────────────────────────────────────────────────
  // PHP magic/verify requires both token and uid (from the link URL)
  const verifyMagicToken = useCallback(async (token, uid) => {
    const res = await fetch(`${PHP_URL}/auth/magic/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, ...(uid != null ? { uid } : {}) }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Invalid link");
    applyAuthResponse(data);
    return data;
  }, [applyAuthResponse]);

  // ── Persisted campaign setter ──────────────────────────────────────────────
  const selectCampaign = useCallback((campaign) => {
    setSelectedCampaign(campaign);
    saveSelectedCampaignId(campaign?.id ?? null);
  }, []);

  // ── Refresh user profile from /auth/me ────────────────────────────────────
  const refreshUser = useCallback(async () => {
    const token = await getValidToken();
    if (!token) return;
    try {
      const res = await fetch(`${PHP_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const { campaigns: camps, ...userFields } = await res.json();
      setUser({ ...userFields, superuser: !!userFields.superuser });
      if (Array.isArray(camps)) setCampaigns(camps);
    } catch { /* ignore */ }
  }, [getValidToken]);

  // ── Re-fetch campaigns and optionally select a specific one ───────────────
  const refreshCampaigns = useCallback(async (selectId = null) => {
    const token = await getValidToken();
    if (!token) return;
    try {
      const res = await fetch(`${PHP_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const { campaigns: camps, ...userFields } = await res.json();
      setUser({ ...userFields, superuser: !!userFields.superuser });
      const campList = Array.isArray(camps) ? camps : [];
      setCampaigns(campList);
      const pick = selectId
        ? (campList.find((c) => c.id === selectId) ?? pickCampaign(campList))
        : pickCampaign(campList);
      setSelectedCampaign(pick);
      saveSelectedCampaignId(pick?.id ?? null);
    } catch { /* ignore */ }
  }, [getValidToken]);

  // ── Logout ─────────────────────────────────────────────────────────────────
  const logout = useCallback(() => {
    clearTokens();
    localStorage.removeItem(LS_CAMPAIGN);
    setUser(null);
    setCampaigns([]);
    setSelectedCampaign(null);
  }, []);

  // ── On mount: check for magic token, then try stored tokens ───────────────
  useEffect(() => {
    const init = async () => {
      // 1. Magic link
      const magicData = initialMagicToken.current;
      if (magicData) {
        try {
          await verifyMagicToken(magicData.token, magicData.uid);
          const deepLink = sessionStorage.getItem("pollis_deep_link");
          sessionStorage.removeItem("pollis_deep_link");
          window.history.replaceState({}, "", deepLink || "/");
          setLoading(false);
          return;
        } catch { /* fall through to normal flow */ }
      }

      // 2. Existing access token
      const at = getAccessToken();
      if (at && !isExpired(at)) {
        const p = parseJwt(at);
        if (p?.sub) {
          try {
            const res = await fetch(`${PHP_URL}/auth/me`, {
              headers: { Authorization: `Bearer ${at}` },
            });
            if (res.ok) {
              applyMeResponse(await res.json());
              setLoading(false);
              return;
            }
          } catch {}
        }
      }

      // 3. Try refresh
      const refreshed = await doRefresh();
      if (refreshed) {
        const at2 = getAccessToken();
        try {
          const res = await fetch(`${PHP_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${at2}` },
          });
          if (res.ok) {
            applyMeResponse(await res.json());
          }
        } catch {}
      }

      setLoading(false);
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{
      user, campaigns, selectedCampaign, setSelectedCampaign: selectCampaign,
      loading, login, logout, refreshUser, refreshCampaigns, verifyMagicToken, getValidToken, authFetch,
      isLoggedIn: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
